import { useState, useEffect } from 'react';

import type { ContentSummary, AgentSearchRequest, AgentMessage } from '~/types/agent';
import type { SearchResponse } from '~/types/search';

import { useWebSocketRequest } from '../api/useWebSocketRequest';
import { isFinalMessage, isSearchEndMessage, isSearchSummaryEndMessage, isCompleteMessage, isErrorMessage } from '../api/utils';
import { getOrchestratorWsUrl } from '../utils/orchestrator-url';

interface UseAgentSearchOptions {
  language: string;
  initialQuery: string;
  initialIncludeSummary: boolean;
  onSearchResults?: (totalHits: number, originalQuery: string) => void;
}

interface UseAgentSearchResult {
  improvedQuery: string | undefined;
  searchResponse: SearchResponse | null;
  searchSummary: ContentSummary | undefined;
  isLoadingSearchResponse: boolean;
  isLoadingSummary: boolean;
  hasReceivedSummary: boolean;
  useFallback: boolean;
  search: (message: string, includeSummary: boolean) => void;
  refreshSummary: (message: string) => void;
  reset: () => void;
}

export function useAgentSearch({
  language,
  initialQuery,
  initialIncludeSummary,
  onSearchResults,
}: UseAgentSearchOptions): UseAgentSearchResult {
  const [improvedQuery, setImprovedQuery] = useState<string>();
  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(null);
  const [searchSummary, setSearchSummary] = useState<ContentSummary | undefined>();
  const [isLoadingSearchResponse, setIsLoadingSearchResponse] = useState(!!initialQuery);
  const [isLoadingSummary, setIsLoadingSummary] = useState(initialIncludeSummary && !!initialQuery);
  const [hasReceivedSummary, setHasReceivedSummary] = useState(false);
  const [useFallback, setUseFallback] = useState(false);

  const { start } = useWebSocketRequest<AgentSearchRequest, AgentMessage>({
    url: `${getOrchestratorWsUrl()}/api/agent/search/ws`,
    isFinalMessage,
    timeoutMs: 30_000,
    onMessage: message => {
      if (isSearchEndMessage(message)) {
        setIsLoadingSearchResponse(false);
        setSearchResponse(message.data.searchResults);
        setImprovedQuery(message.data.searchResults.searchQuery);
        onSearchResults?.(message.data.searchResults.totalHits, message.data.searchResults.originalQuery);
      } else if (isSearchSummaryEndMessage(message)) {
        setIsLoadingSummary(false);
        setHasReceivedSummary(true);
        setSearchSummary(message.data?.contentSummary);
      } else if (isCompleteMessage(message)) {
        setIsLoadingSearchResponse(false);
        setSearchResponse(message.data);
        setImprovedQuery(message.data.searchQuery);
        setIsLoadingSummary(false);
        setHasReceivedSummary(true);
        setSearchSummary(message.data?.dynamicBox);
        onSearchResults?.(message.data.totalHits, message.data.originalQuery);
      } else if (isErrorMessage(message) && message.data.code !== 408) {
        setIsLoadingSearchResponse(false);
        setSearchResponse(null);
        setIsLoadingSummary(false);
        setHasReceivedSummary(false);
        setSearchSummary(undefined);
        setUseFallback(true);
      }
    },
    onAborted: () => {
      setIsLoadingSearchResponse(false);
      setIsLoadingSummary(false);
      setUseFallback(true);
    },
  });

  useEffect(() => {
    if (initialQuery) {
      start({ message: initialQuery, language, include_summary: initialIncludeSummary });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- kun ved mount
  }, []);

  const reset = (): void => {
    setImprovedQuery(undefined);
    setSearchResponse(null);
    setSearchSummary(undefined);
    setHasReceivedSummary(false);
  };

  const search = (message: string, includeSummary: boolean): void => {
    if (useFallback) {
      return;
    }
    setIsLoadingSearchResponse(true);
    setIsLoadingSummary(includeSummary);
    setHasReceivedSummary(false);
    start({ message, language, include_summary: includeSummary });
  };

  const refreshSummary = (message: string): void => {
    setIsLoadingSummary(true);
    setHasReceivedSummary(false);
    start({ message, language, include_summary: true });
  };

  return {
    improvedQuery,
    searchResponse,
    searchSummary,
    isLoadingSearchResponse,
    isLoadingSummary,
    hasReceivedSummary,
    useFallback,
    search,
    refreshSummary,
    reset,
  };
}
